import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { XMarkIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface ProviderBreakdown {
  calls: number;
  units: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
}

interface UserBreakdown {
  user_id: number;
  username: string;
  calls: number;
  cost_usd: number;
}

interface UsageResponse {
  account_id?: number;
  account_name?: string;
  user_id?: number;
  username?: string;
  date_range: { start: string; end: string; days: number };
  totals: { cost_usd: number; calls: number };
  by_provider: Record<string, ProviderBreakdown>;
  by_user?: UserBreakdown[];
}

interface Props {
  mode: 'account' | 'user';
  targetId: number;
  /** Used for the title; the actual data comes from the API response. */
  targetLabel: string;
  onClose: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const fmtUsd = (n: number) =>
  '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const PROVIDER_LABELS: Record<string, string> = {
  reducto: 'Reducto OCR',
  openai: 'OpenAI (GPT-4o-mini)',
};

const UsageBreakdownModal: React.FC<Props> = ({ mode, targetId, targetLabel, onClose }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('auth_token');
        const url = `${API_BASE_URL}/admin/usage/${mode}/${targetId}?days=${days}`;
        const resp = await axios.get(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setData(resp.data);
      } catch (e: any) {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e?.message || 'Failed to load usage';
        setError(msg);
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [mode, targetId, days]);

  const providers = data ? Object.keys(data.by_provider).sort() : [];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            <ChartBarIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            <div>
              <h3 className="text-lg font-semibold dark:text-white">
                API Usage — {targetLabel}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Reducto OCR + OpenAI LLM cost breakdown
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Range selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Date range:
            </label>
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 text-xs font-medium rounded ${
                  days === d
                    ? 'bg-brand-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Last {d}d
              </button>
            ))}
          </div>

          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          {!loading && data && (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg border dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total cost</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {fmtUsd(data.totals.cost_usd)}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg border dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total API calls</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {data.totals.calls.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Per-provider */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  By provider
                </h4>
                {providers.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No usage in this period.
                  </p>
                ) : (
                  <div className="overflow-hidden border dark:border-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/40">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">Provider</th>
                          <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 text-right">Calls</th>
                          <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 text-right">Units</th>
                          <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 text-right">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {providers.map((p) => {
                          const v = data.by_provider[p];
                          const unitLabel =
                            p === 'reducto' ? `${v.units} pages` :
                            p === 'openai' ? `${v.units.toLocaleString()} tokens` :
                            v.units.toLocaleString();
                          return (
                            <tr key={p} className="border-t dark:border-gray-700 text-gray-900 dark:text-gray-100">
                              <td className="px-3 py-2">{PROVIDER_LABELS[p] || p}</td>
                              <td className="px-3 py-2 text-right">{v.calls}</td>
                              <td className="px-3 py-2 text-right text-xs text-gray-600 dark:text-gray-400">{unitLabel}</td>
                              <td className="px-3 py-2 text-right font-medium">{fmtUsd(v.cost_usd)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Per-user (only in account mode) */}
              {mode === 'account' && data.by_user && data.by_user.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    By user
                  </h4>
                  <div className="overflow-hidden border dark:border-gray-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-900/40">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300">User</th>
                          <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 text-right">Calls</th>
                          <th className="px-3 py-2 font-medium text-gray-700 dark:text-gray-300 text-right">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.by_user.map((u) => (
                          <tr key={u.user_id} className="border-t dark:border-gray-700 text-gray-900 dark:text-gray-100">
                            <td className="px-3 py-2 font-mono text-xs">{u.username}</td>
                            <td className="px-3 py-2 text-right">{u.calls}</td>
                            <td className="px-3 py-2 text-right font-medium">{fmtUsd(u.cost_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                Range: {new Date(data.date_range.start).toLocaleDateString()} – {new Date(data.date_range.end).toLocaleDateString()}
              </p>
            </>
          )}
        </div>

        <div className="flex justify-end px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsageBreakdownModal;
